import { TimeLineLabel } from 'utils/components/TimeSeriesChart/timeline';
import {
    KeyedStreamItem,
    StreamElementTimeSeriesSessionBoundaryItem,
} from 'communication/types/StreamElement';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';

export interface TimeSeriesDisableRange {
    // Epoch from time
    from: number;
    // Epoch to time
    to: number;
}

export interface TimeSeriesDisableRangeLabel {
    open: number;
    openLabel: string;
    close?: number;
    closeLabel?: string;
}

export interface TimeSeriesSessionLabelDataItem
    extends TimeSeriesChartDataItem {
    sessionBreakAnnotation?: boolean;
}

export interface SessionBoundaryDataTimeSeriesItem extends KeyedStreamItem {
    sessionBoundaryLabel?: string;
    sessionBoundaryDate?: string;
}

export const mapSessionBoundary: (
    from: StreamElementTimeSeriesSessionBoundaryItem
) => SessionBoundaryDataTimeSeriesItem = (from) => ({
    _i: from._i,
    sessionBoundaryLabel: from.si,
    sessionBoundaryDate: from.di,
});

export function mapFromElementRangeArray(
    disableRange: number[] | undefined
): TimeSeriesDisableRange[] {
    if (!disableRange) {
        return [];
    }
    const pairs: TimeSeriesDisableRange[] = [];
    for (let i = 0; i < disableRange.length; i += 2) {
        pairs.push({ from: disableRange[i], to: disableRange[i + 1] });
    }
    return pairs;
}

export function getRangeLabels(
    timeSeries: SessionBoundaryDataTimeSeriesItem[],
    breaksInTrading: TimeSeriesDisableRange[]
): TimeSeriesDisableRangeLabel[] {
    const ret: TimeSeriesDisableRangeLabel[] = [];
    for (const br of breaksInTrading) {
        const itemWithLabelForStart = timeSeries.find((item) =>
            itemHasLabelForForStartOfBreak(item, br)
        );
        const itemWithLabelForEnd = timeSeries.find((item) =>
            itemHasLabelForEndOfBreak(item, br)
        );

        ret.push({
            close: itemWithLabelForStart
                ? itemWithLabelForStart._i * 60000
                : br.from,
            open: br.to,
            closeLabel: itemWithLabelForStart
                ? itemWithLabelForStart.sessionBoundaryLabel
                : undefined,
            openLabel: itemWithLabelForEnd?.sessionBoundaryLabel ?? '',
        });
    }
    return ret;
}

export function getTimeLineLabels(
    timeSeries: SessionBoundaryDataTimeSeriesItem[],
    breaksInTrading: TimeSeriesDisableRange[],
    mappedTimeSeries: TimeSeriesSessionLabelDataItem[]
): TimeLineLabel[] {
    const isForABreak = (
        item: SessionBoundaryDataTimeSeriesItem,
        disableRange: TimeSeriesDisableRange[]
    ) => {
        const isLabelForStart = disableRange.find((br) =>
            itemHasLabelForForStartOfBreak(item, br)
        );
        const isLabelForEnd = disableRange.find((br) =>
            itemHasLabelForEndOfBreak(item, br)
        );
        return isLabelForStart || isLabelForEnd;
    };

    return timeSeries
        .filter(
            (t) => !!t.sessionBoundaryLabel && !isForABreak(t, breaksInTrading)
        )
        .map((t) => toTimeLineLabel(t, mappedTimeSeries));
}

function toTimeLineLabel(
    item: SessionBoundaryDataTimeSeriesItem,
    mappedTimeSeries: TimeSeriesSessionLabelDataItem[]
): TimeLineLabel {
    const time = item._i * 60000;
    const from = time;
    const to = time + 59999;
    const label = item.sessionBoundaryLabel!;

    const padding = 29999;

    mappedTimeSeries.find((t) => t.time === time)!.sessionBreakAnnotation =
        true;

    return {
        time: item._i * 60000,
        from: from - padding,
        label,
        to: to + padding,
    };
}

function itemHasLabelForForStartOfBreak(
    item: StreamElementTimeSeriesSessionBoundaryItem,
    br: TimeSeriesDisableRange
) {
    return item._i * 60000 + 60000 === br.from;
}

function itemHasLabelForEndOfBreak(
    item: StreamElementTimeSeriesSessionBoundaryItem,
    br: TimeSeriesDisableRange
) {
    return item._i * 60000 === br.to;
}
